import React from "react";
import { maxCharsForWidth, truncate, wrapText } from "./truncate";

// Shared font sizes. Source sizes chosen so that after the SVG scales down
// into a 768px card (natural viewBox often 900-1200px wide), text is still
// legible — post-scale ~10-11 CSS px. At the previous 9/10 source the
// scaled rendering dipped to 6-7px and read as blurry.
export const FS_LABEL = 13;
export const FS_DATE = 12;

// Markers used by the float + arrow decorations. Put once per outer <svg>
// (in the combined component, both sections reuse the same marker ids).
export function TimelineLayoutDefs() {
  return (
    <defs>
      <marker
        id="td-arrow"
        markerWidth="8"
        markerHeight="8"
        refX="8"
        refY="4"
        orient="auto"
        markerUnits="strokeWidth"
      >
        <path d="M0,0 L0,8 L8,4 z" fill="#1f2937" />
      </marker>
      <marker
        id="td-float-end"
        markerWidth="4"
        markerHeight="4"
        refX="2"
        refY="2"
        orient="auto"
        markerUnits="strokeWidth"
      >
        <path d="M0,0 L0,4 L4,4 L4,0 z" fill="black" />
      </marker>
    </defs>
  );
}

type Arrow = { x1: number; x2: number; type: "material" | "submittal" };

/**
 * Renders the body of a timeline diff — everything inside `<svg>` except the
 * `<defs>`. Returned as a fragment so it can be wrapped by either a
 * single-layout `<svg>` (TimelineDiff) or a grouped-by-y `<g>` inside a
 * combined Before+After `<svg>` (CombinedTimelineDiff).
 *
 * Keep this pure (no hooks) so it works from both React renders and an
 * offscreen rasterization pass.
 */
export function renderTimelineLayout(
  layout: any,
  options: { arrow?: Arrow; isCompletedWorkflow: boolean; keyPrefix?: string }
): React.ReactNode {
  const { arrow, isCompletedWorkflow, keyPrefix = "" } = options;

  return (
    <>
      {/* primary track */}
      <rect
        x={layout.primaryTrack.originX}
        y={layout.primaryTrack.originY}
        width={layout.primaryTrack.width}
        height={layout.primaryTrack.height}
        fill="#ffffff"
        stroke="#111"
        strokeWidth={1}
      />

      {/* milestone markers (vertical tick lines) */}
      {layout.milestoneMarkers.map((m: any, i: number) => (
        <line
          key={`${keyPrefix}mm-${i}`}
          x1={m.originX}
          y1={m.originY}
          x2={m.originX}
          y2={m.originY + m.height}
          className={m.className}
          strokeWidth={2}
        />
      ))}

      {/* special labels (activity names) — wrap up to 2 lines before
          truncating. Aligned to the bottom of the label box; the first
          line's baseline is the original position, a second line stacks
          ABOVE it so we don't spill below the track. */}
      {layout.specialLabels.map((l: any, i: number) => {
        const chars = maxCharsForWidth(l.width - 8, FS_LABEL);
        const lines = wrapText(l.label, chars, 2);
        const baseY = l.originY + l.height - 4;
        return (
          <text
            key={`${keyPrefix}sl-${i}`}
            x={l.originX + 4}
            y={baseY - (lines.length - 1) * (FS_LABEL + 2)}
            fontSize={FS_LABEL}
            fill="#374151"
          >
            {lines.map((line, idx) => (
              <tspan
                key={idx}
                x={l.originX + 4}
                dy={idx === 0 ? 0 : FS_LABEL + 2}
              >
                {line}
              </tspan>
            ))}
          </text>
        );
      })}

      {/* milestone date + name labels (2-line) */}
      {layout.labels.map((l: any, i: number) => {
        const nameChars = maxCharsForWidth(l.width - 4, FS_LABEL);
        const dateChars = maxCharsForWidth(l.width - 4, FS_DATE);
        return (
          <g
            key={`${keyPrefix}l-${i}`}
            transform={`translate(${l.originX}, ${l.originY})`}
          >
            <text
              x={2}
              y={FS_DATE + 1}
              fontSize={FS_DATE}
              fontWeight="bold"
              fill="#111"
            >
              {truncate(l.date, dateChars)}
            </text>
            <text
              x={2}
              y={FS_DATE + FS_LABEL + 4}
              fontSize={FS_LABEL}
              fill="#374151"
            >
              {wrapText(l.label, nameChars, 2).map((line, idx) => (
                <tspan key={idx} x={2} dy={idx === 0 ? 0 : FS_LABEL + 2}>
                  {line}
                </tspan>
              ))}
            </text>
          </g>
        );
      })}

      {/* material (post) tracks — name inside box, date below */}
      {layout.postTracks.map((t: any, i: number) => {
        // Name truncates to 1 line inside the box (the spec name shows in
        // the grid column already, so a wrap-clipped material name here
        // is redundant). Date renders below the box with a generous
        // pixel budget so narrow bars don't clip strings like
        // "Material Delivered : 09-09-2026".
        const labelChars = maxCharsForWidth(t.width - 8, FS_LABEL);
        const dateWidth = Math.max(t.width, 240);
        const dateChars = maxCharsForWidth(dateWidth - 8, FS_DATE);
        const dateY = t.originY + t.height + 14;
        return (
          <g key={`${keyPrefix}pt-${i}`}>
            <rect
              x={t.originX}
              y={t.originY}
              width={t.width}
              height={t.height}
              fill="#ffffff"
              stroke="#111"
              strokeWidth={1}
            />
            <text
              x={t.originX + 4}
              y={t.originY + 14}
              fontSize={FS_LABEL}
              fill="#111"
            >
              <title>{t.label}</title>
              {truncate(t.label, labelChars)}
            </text>
            <text x={t.originX} y={dateY} fontSize={FS_DATE} fill="#6b7280">
              <title>{t.endLabel}</title>
              {truncate(t.endLabel, dateChars)}
            </text>
          </g>
        );
      })}

      {/* float indicator */}
      {layout.float.days <= 0 &&
        (() => {
          // Extend the exhausted-float band downward so it covers the
          // post-track caption ("Material Delivered : …") that sits
          // below the bar — keeps the visual association between the
          // negative float and the late material delivery.
          const postTrackBottom = layout.postTracks.length
            ? Math.max(
                ...layout.postTracks.map(
                  (t: any) => t.originY + t.height + 14 + 4
                )
              )
            : layout.float.originY + layout.float.height;
          const height = Math.max(
            layout.float.height,
            postTrackBottom - layout.float.originY
          );
          return (
            <rect
              x={layout.float.originX}
              y={layout.float.originY}
              width={layout.float.width}
              height={height}
              fill="#ff000033"
              style={{ pointerEvents: "none" }}
            />
          );
        })()}
      {layout.float.days !== 0 && (
        <>
          <line
            x1={layout.float.originX + 2}
            y1={140}
            x2={layout.float.originX + layout.float.width - 2}
            y2={140}
            stroke="black"
            strokeDasharray="2,3"
            markerStart="url(#td-float-end)"
            markerEnd="url(#td-float-end)"
          />
          <text
            x={layout.float.originX + layout.float.width / 2}
            y={140 - 4}
            textAnchor="middle"
            fontSize={FS_LABEL}
            fill="#111"
          >
            Float {layout.float.days} days
          </text>
        </>
      )}
      {layout.float.days === 0 && (
        <>
          <rect
            x={layout.float.originX - 2}
            y={140}
            width={4}
            height={4}
            fill="black"
          />
          <text
            x={layout.float.originX + 6}
            y={140 + 10}
            fontSize={FS_LABEL}
            fill="#111"
          >
            No float
          </text>
        </>
      )}
      <line
        x1={layout.float.originX}
        y1={layout.float.originY}
        x2={layout.float.originX}
        y2={layout.float.originY + layout.float.height}
        stroke="black"
        strokeDasharray="1,2"
      />
      <line
        x1={layout.float.originX + layout.float.width}
        y1={layout.float.originY}
        x2={layout.float.originX + layout.float.width}
        y2={layout.float.originY + layout.float.height}
        stroke="black"
        strokeDasharray="1,2"
      />

      {/* arrow (after→before alignment indicator) */}
      {arrow && arrow.type === "submittal" && (
        <line
          x1={arrow.x1 + 10}
          x2={arrow.x2 + 10}
          y1={layout.primaryTrack.originY + 20}
          y2={layout.primaryTrack.originY + 20}
          stroke="#1f2937"
          strokeWidth={1.5}
          markerEnd="url(#td-arrow)"
        />
      )}

      {/* today indicator (omitted for completed workflows) */}
      {!isCompletedWorkflow &&
        (() => {
          // CSS module classes ("todayIndicator" / "todayIndicatorLate")
          // don't reach the rasterize sandbox, so colour + 45° rotation
          // are inlined here instead. Late = red, on-time = blue —
          // mirrors the original SubmittalDiffV6 viz.
          const isLate = String(layout.today.className ?? "").includes("Late");
          const fillColor = isLate ? "red" : "dodgerblue";
          const strokeColor = isLate ? "brown" : "darkblue";
          const cx = layout.today.originX;
          const cy = layout.today.originY;
          return (
            <>
              <rect
                x={cx - layout.today.width / 2}
                y={cy - layout.today.height / 2}
                width={layout.today.width}
                height={layout.today.height}
                rx={2}
                fill={fillColor}
                stroke={strokeColor}
                transform={`rotate(45 ${cx} ${cy})`}
              />
              <line
                x1={layout.today.originX}
                y1={layout.today.originY}
                x2={layout.today.originX}
                y2={layout.today.originY - 60}
                stroke="black"
                strokeDasharray="1,2"
              />
              <text
                x={layout.today.originX + 4}
                y={layout.today.originY - 46}
                fontSize={FS_DATE}
                fontWeight="bold"
                fill="#111"
              >
                {truncate(
                  layout.today.date,
                  maxCharsForWidth(layout.today.labelWidth - 8, FS_DATE)
                )}
              </text>
              <text
                x={layout.today.originX + 4}
                y={layout.today.originY - 34}
                fontSize={FS_LABEL}
                fill="#374151"
              >
                {truncate(
                  layout.today.label,
                  maxCharsForWidth(layout.today.labelWidth - 8, FS_LABEL)
                )}
              </text>
              {layout.todayToNextPlannedMilestone && (
                <line
                  x1={layout.todayToNextPlannedMilestone.originX + 3}
                  y1={layout.today.originY}
                  x2={
                    layout.todayToNextPlannedMilestone.originX +
                    layout.todayToNextPlannedMilestone.width -
                    layout.today.width
                  }
                  y2={layout.today.originY}
                  stroke="black"
                  strokeDasharray="2,3"
                  markerStart="url(#td-float-end)"
                  markerEnd="url(#td-float-end)"
                />
              )}
            </>
          );
        })()}
    </>
  );
}
